<template>
  <d-modal
    :class="{ 'day-popup': true, 'two-days-mode': twoDaysMode }"
    size="lg">
    <d-modal-header>
      <div class="w-100 d-flex">
        <div class="text-center w-50 px-3">
          <d-datepicker
            v-model="firstDayDate"
            :monday-first="true"
            format="D, d MMMM yyyy"
            :placeholder="$t('ui.chooseDate')"
            :language="translationsForDatePicker" />

          <small
            v-if="firstQty"
            class="d-block mt-2">
            {{ $t('calendar.planned') }}:
            <span :class="{
              'text-danger': firstQty > 3500,
              'text-warning': firstQty > 2500 && firstQty <= 3500,
              'text-success': firstQty <= 2500
            }">
              {{ firstQty }} {{ $t('ui.piecesAbbr') }}
            </span>
          </small>
        </div>

        <div class="text-center w-50 px-3">
          <d-button
            v-if="!showDaySelection"
            class="ml-auto mr-4 w-100"
            theme="secondary"
            @click.prevent="showDaySelection = true; twoDaysMode = true;">
            {{ $t('calendar.chooseSecondDay') }}
          </d-button>

          <d-datepicker
            v-if="showDaySelection"
            v-model="secondDayDate"
            :monday-first="true"
            format="D, d MMMM yyyy"
            :placeholder="$t('ui.chooseDate')"
            :language="translationsForDatePicker" />

          <small
            v-if="secondQty"
            class="d-block mt-2">
            {{ $t('calendar.planned') }}:
            <span :class="{
              'text-danger': secondQty > 3500,
              'text-warning': secondQty > 2500 && secondQty <= 3500,
              'text-success': secondQty <= 2500
            }">
              {{ secondQty }} {{ $t('ui.piecesAbbr') }}
            </span>
          </small>
        </div>

        <d-button
          class="day-popup-close"
          @click.prevent="handleClose">
          <i class="material-icons">close</i>
        </d-button>
      </div>
    </d-modal-header>
    <d-modal-body class="p-0">
      <div class="d-flex">
        <div class="mr-3 w-100">
          <d-alert
            :show="datesAreTheSame"
            theme="danger"
            class="text-center">
            {{ $t('calendar.needToSelectDifferentDates') }}
          </d-alert>
          <table
            v-if="!datesAreTheSame"
            :class="{
              'table': true,
              'w-100': true,
              'load-in-progress': refreshInProgress || itemsOrderSaveInProgress
            }">
            <thead>
              <tr>
                <th style="width: 40px;"></th>
                <th>{{ $t('ui.order') }}</th>
                <th style="width: 100px;" class="text-right">{{ $t('ui.amount') }}</th>
                <th style="width: 180px;" class="text-center">{{ $t('status.status') }}</th>
                <th style="width: 48px;"></th>
              </tr>
            </thead>

            <draggable
              v-model="firstDayItems"
              group="days"
              tag="tbody">
              <tr
                v-for="(item, index) of firstDayItems"
                :key="'order-item-' + index">
                <td
                  style="width: 40px; opacity: .65;"
                  class="align-middle text-center is-draggable px-0">
                  <i class="material-icons">menu</i>
                </td>
                <td class="align-middle">
                  {{ item.order_name }}
                </td>
                <td style="width: 100px;" class="text-right align-middle">
                  {{ item.quantity }} {{ $t('ui.piecesAbbr') }}
                </td>
                <td
                  style="width: 180px;"
                  :class="{
                    'text-center': true,
                    'align-middle': true,
                    'has-special-color': item.order_status === 'TO_CHECK'
                  }">
                  <d-badge
                    :style="getOrderStatusStyle(item.order_status, item.order_is_priority)">
                    {{ getOrderStatus(item.order_status) }}
                  </d-badge>
                </td>
                <td style="width: 48px;">
                  <d-button
                    style="primary"
                    size="sm"
                    outline
                    @click.prevent="showMovePopup(item.id, item.quantity)">
                    <strong>
                    <i class="material-icons">
                      trending_flat
                    </i>
                    </strong>
                  </d-button>
                </td>
              </tr>
              <tr v-if="firstLoaded && !firstDayItems.length">
                <td colspan="5" class="p-0 text-center">
                  <d-alert
                    show
                    theme="warning">
                    {{ $t('calendar.noPlannedOrders') }}
                  </d-alert>
                </td>
              </tr>
            </draggable>
          </table>
        </div>

        <div
          v-if="twoDaysMode"
          class="ml-3 w-100">
          <d-alert
            :show="datesAreTheSame"
            theme="danger"
            class="text-center">
            {{ $t('calendar.needToSelectDifferentDates') }}
          </d-alert>

          <table
            v-if="!datesAreTheSame"
            :class="{
              'table': true,
              'w-100': true,
              'load-in-progress': refreshInProgress || itemsOrderSaveInProgress
            }">
            <thead>
              <tr>
                <th style="width: 40px;"></th>
                <th>{{ $t('ui.order') }}</th>
                <th style="width: 100px;" class="text-right">{{ $t('ui.amount') }}</th>
                <th style="width: 180px;" class="text-center">{{ $t('status.status') }}</th>
                <th style="width: 48px;"></th>
              </tr>
            </thead>

            <draggable
              v-model="secondDayItems"
              :group="secondDayDate ? 'days' : 'nope'"
              tag="tbody">
              <tr
                v-for="(item, index) of secondDayItems"
                :key="'order-item-' + index">
                <td
                  style="width: 40px; opacity: .65;"
                  class="align-middle text-center is-draggable px-0">
                  <i class="material-icons">menu</i>
                </td>
                <td class="align-middle">
                  {{ item.order_name }}
                </td>
                <td style="width: 100px;" class="text-right align-middle">
                  {{ item.quantity }} {{ $t('ui.piecesAbbr') }}
                </td>
                <td
                  style="width: 180px;"
                  :class="{
                    'text-center': true,
                    'align-middle': true,
                    'has-special-color': item.order_status === 'TO_CHECK'
                  }">
                  <d-badge
                    :style="getOrderStatusStyle(item.order_status, item.order_is_priority)">
                    {{ getOrderStatus(item.order_status) }}
                  </d-badge>
                </td>
                <td style="width: 48px;">
                  <d-button
                    style="primary"
                    size="sm"
                    outline
                    @click.prevent="showMovePopup(item.id, item.quantity)">
                    <strong>
                    <i class="material-icons">
                      trending_flat
                    </i>
                    </strong>
                  </d-button>
                </td>
              </tr>
              <tr v-if="twoDaysMode && !secondDayItems.length && !secondLoaded">
                <td colspan="5" class="p-0">
                  <d-alert
                    show
                    class="text-center"
                    theme="light">
                    {{ $t('calendar.chooseDayToShowOrders') }}
                  </d-alert>
                </td>
              </tr>

              <tr v-if="!secondDayItems.length && secondLoaded">
                <td colspan="5" class="p-0">
                  <d-alert
                    show
                    class="text-center"
                    theme="warning">
                    {{ $t('calendar.noPlannedOrders') }}
                  </d-alert>
                </td>
              </tr>
            </draggable>
          </table>
        </div>
      </div>
    </d-modal-body>
  </d-modal>
</template>

<script>
import CalendarUtils from './../../utils/CalendarUtils';
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import orderStatuses from './../../data/order-statuses.js';
import datepickerTranslations from './../../data/datepicker-translations.js';
import vueDraggable from 'vuedraggable';
import Vue from 'vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'day-popup',
  props: [
    'date',
    'filters'
  ],
  components: {
    'draggable': vueDraggable
  },
  computed: {
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    firstQty () {
      if (this.firstDayItems.length) {
        return this.firstDayItems.reduce((sum, item) => sum + item.quantity, 0);
      }

      return 0;
    },
    secondQty () {
      if (this.secondDayItems.length) {
        return this.secondDayItems.reduce((sum, item) => sum + item.quantity, 0);
      }

      return 0;
    },
    datesAreTheSame () {
      if (!this.firstDayDate || !this.secondDayDate) {
        return false;
      }

      return CalendarUtils.formatDateForPHP(this.firstDayDate) === CalendarUtils.formatDateForPHP(this.secondDayDate);
    },
    allItems () {
      return [
        this.firstDayItems.map(item => item.id).join(','),
        this.secondDayItems.map(item => item.id).join(',')
      ].join('||');
    }
  },
  data () {
    return {
      // first column
      firstDayDate: null,
      firstDayItems: [],
      firstLoaded: false,
      // second column
      secondDayItems: [],
      secondDayDate: null,
      secondLoaded: false,
      // other flags
      itemsOrderSaveInProgress: false,
      refreshInProgress: true,
      moveInProgress: false,
      twoDaysMode: false,
      showDaySelection: false,
      loadedItems: null,
      loadError: false
    };
  },
  mounted () {
    this.firstDayDate = new Date(this.date);
    this.$bus.$on('services-schedule-day-popup-refresh', this.refreshContent);
    this.$bus.$on('services-schedule-update-day-popup', this.refreshContent);
    this.loadDay('first');

    this.$watch('firstDayDate', () => {
      if (!this.refreshInProgress && !this.itemsOrderSaveInProgress) {
        this.loadDay('first');
      }
    });

    this.$watch('secondDayDate', () => {
      if (!this.refreshInProgress && !this.itemsOrderSaveInProgress) {
        this.loadDay('second');
      }
    });

    this.$watch('allItems', (newValue, oldValue) => {
      Vue.nextTick(() => {
        if (
          !this.refreshInProgress &&
          !this.itemsOrderSaveInProgress &&
          !this.moveInProgress &&
          (this.firstLoaded || this.secondLoaded) &&
          (this.firstDayItems.length || this.secondDayItems.length)
        ) {
          let itemsBefore = oldValue.split('||').map(column => column.split(',').length);
          let itemsAfter = newValue.split('||').map(column => column.split(',').length);

          if (itemsBefore[0] !== itemsAfter[0] || itemsBefore[1] !== itemsAfter[1]) {
            this.moveInProgress = true;
            this.doMove();
          } else {
            this.itemsOrderSaveInProgress = true;
            this.debouncedSaveItemsOrdering();
          }
        }
      });
    });
  },
  methods: {
    handleClose () {
      this.$bus.$emit('services-schedule-close-day-popup');
    },
    humanDateFormat (date) {
      let dateText = CalendarUtils.humanDateFormat(date, false, true);
      return dateText.charAt(0).toLocaleUpperCase() + dateText.slice(1);
    },
    // eslint-disable-next-line
    debouncedSaveItemsOrdering: debounce(function () {
      this.saveItemsOrder();
    }, 1000),
    saveItemsOrder () {
      this.itemsOrderSaveInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/services-schedule/ordering',
        formData: this.generateOrderingData(),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('calendar.orderChangeUnsuccessful'),
        successAction: () => {
          this.itemsOrderSaveInProgress = false;
          this.$bus.$emit('services-schedule-calendar-refresh');
        }
      });
    },
    generateOrderingData () {
      let output = {
        dates: []
      };

      if (this.firstDayItems.length) {
        output.dates.push({
          date: CalendarUtils.formatDateForPHP(this.firstDayDate),
          items: this.firstDayItems.map(item => item.id)
        });
      }

      if (this.secondDayDate && this.secondDayItems.length) {
        output.dates.push({
          date: CalendarUtils.formatDateForPHP(this.secondDayDate),
          items: this.secondDayItems.map(item => item.id)
        });
      }

      return output;
    },
    showMovePopup (itemID, maxQty) {
      this.$bus.$emit('services-schedule-open-move-popup', itemID, maxQty, this.date);
    },
    refreshContent () {
      this.loadDay('first');

      if (this.secondDayDate) {
        this.loadDay('second');
      }
    },
    getOrderStatus (status) {
      return orderStatuses[this.$i18n.locale][status] ? orderStatuses[this.$i18n.locale][status] : '??';
    },
    getOrderStatusStyle (status, hasPriority) {
      switch (status) {
        case 'TO_COLLECT': return 'dark';
        case 'WAITING_PURCHASE_PRODUCTS': return hasPriority ? 'danger' : 'primary';
        case 'ORDER': return hasPriority ? 'danger' : 'primary';
        case 'IN_PRODUCTION': return hasPriority ? 'danger' : 'primary';
        case 'TO_CHECK': return 'secondary';
        case 'PREPARATION_FOR_SHIPPING': return 'success';
        case 'WAITING_FOR_SHIPPING': return 'success';
        case 'SENT': return 'success';
        case 'PENDING_PAYMENT': return 'success';
        case 'ENDED': return 'success';
        default: return '';
      }
    },
    loadDay (column) {
      let where = {};
      this.refreshInProgress = true;

      if (this.filters.contractor_id !== null && this.filters.contractor_id) {
        where.contractor_id = this.filters.contractor_id;
      }

      if (this.filters.service_type_id !== null && this.filters.service_type_id) {
        where.service_type_id = this.filters.service_type_id;
      }

      if (column === 'first') {
        this.firstLoaded = false;
        where.date_from = CalendarUtils.formatDateForPHP(this.firstDayDate);
        where.date_to = CalendarUtils.formatDateForPHP(this.firstDayDate);
      } else if (column === 'second') {
        this.secondLoaded = false;
        where.date_from = CalendarUtils.formatDateForPHP(this.secondDayDate);
        where.date_to = CalendarUtils.formatDateForPHP(this.secondDayDate);
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: '/services-schedule/items',
        outputKey: 'loadedItems',
        loadedKey: column === 'first' ? 'firstLoaded' : 'secondLoaded',
        params: {
          where
        },
        errorKey: 'loadError',
        successAction: (response) => {
          if (column === 'first') {
            if (response[0]) {
              this.firstDayItems = response[0].items;
            } else {
              this.firstDayItems = [];
            }
          } else if (column === 'second') {
            if (response[0]) {
              this.secondDayItems = response[0].items;
            } else {
              this.secondDayItems = [];
            }
          }

          setTimeout(() => {
            this.refreshInProgress = false;
          }, 0);
        }
      });
    },
    doMove () {
      let firstDay = CalendarUtils.formatDateForPHP(this.firstDayDate);
      let secondDay = CalendarUtils.formatDateForPHP(this.secondDayDate);
      let movedItem = false;
      let dateToUse = false;

      for (let i = 0; i < this.firstDayItems.length; i++) {
        if (this.firstDayItems[i].date !== firstDay) {
          movedItem = this.firstDayItems[i];
          dateToUse = firstDay;
          break;
        }
      }

      if (!movedItem) {
        for (let i = 0; i < this.secondDayItems.length; i++) {
          if (this.secondDayItems[i].date !== secondDay) {
            movedItem = this.secondDayItems[i];
            dateToUse = secondDay;
            break;
          }
        }
      }

      if (movedItem) {
        FormUtils.submitInBackground({
          $http: this.$http,
          endpoint: '/services-schedule/update',
          formData: {
            items: [{
              action: 'update',
              id: movedItem.id,
              date: dateToUse
            }]
          },
          errorTitle: this.$t('ui.errorOccured'),
          errorText: this.$t('calendar.orderUpdateUnsuccessful'),
          successAction: () => {
            this.moveInProgress = false;
            this.$bus.$emit('services-schedule-calendar-refresh');
            this.saveItemsOrder();
          },
          failAction: () => {
            this.moveInProgress = false;
          }
        });
      }

      this.moveInProgress = false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('services-schedule-day-popup-refresh', this.refreshContent);
    this.$bus.$off('services-schedule-update-day-popup', this.refreshContent);
  }
}
</script>

<style lang="scss">
.day-popup {
  .close {
    display: none;
  }

  &.two-days-mode {
    .modal-dialog {
      max-width: 1200px!important;
      width: 1200px!important;
    }
  }

  .modal-header {
    border-bottom: none;
  }

  .day-popup-close {
    background: transparent;
    box-shadow: none!important;
    border: none;
    color: #d22;
    padding: 0;
    position: absolute;
    right: 12px;
    top: 22px;

    &:active,
    &:focus,
    &:hover {
      background: transparent!important;
      box-shadow: none!important;
    }

    i {
      font-size: 24px;
    }
  }

  .is-draggable {
    cursor: move;
  }

  .load-in-progress {
    opacity: .35;
    pointer-events: none;
  }

  td.has-special-color {
    .badge {
      background: #8445f7!important;
    }
  }

  .vdp-datepicker input.form-control {
    text-align: center;
  }

  table td {
    background: #fff;
  }
}
</style>
